/* Root app: routing, tweaks state */
import React from 'react';
import { useTweaks, TweaksPanel, TweakSection, TweakRadio, TweakToggle, TweakColor, TweakButton, TweaksTrigger } from './tweaks-panel.jsx';
import { Sidebar, TopHeader, NAV, BUILD_SHA, BUILD_TIME, BUILD_SHA_SHORT } from './components/shell.jsx';
import { RouteTitleProvider } from './components/route-title-context.jsx';
import { PullToRefresh } from './components/pull-to-refresh.jsx';
import { ChatView } from './components/chat.jsx';
import { WorkersView, SkillsView } from './components/workers.jsx';
import { AgentsView } from './components/agents.jsx';
import { DeckView } from './components/deck-view.jsx';
import { MemoView } from './components/memo-view.jsx';
import { AdminView, AdminLogsView, AdminReadmeView, OnboardingView } from './components/admin.jsx';
import { OrgRepoView, ReportsView } from './components/orgrepo-news-reports-arch.jsx';
import { SlideSandboxView } from './components/slide-sandbox.jsx';
import { NewsView } from './components/news.jsx';
import { LogsView } from './components/logs.jsx';
import { PeopleView, ProjectsView, ClientsView, ServicesView, IndustriesView, OrganizationsView, OfficesView } from './components/directories.jsx';
import { StaffingView } from './components/staffing.jsx';
import { AuthGate } from './components/auth-gate.jsx';
import { logout as apiLogout, fetchUnreadCounts, getPresence, listChats } from './api.js';
import { subscribeReads, getReads, syncReadsFromServer, countUnread } from './lib/chat-reads.js';

const SEEN_LS_KEY = 'erasphere.lastSeen';

const TWEAK_DEFAULTS = {
  density: 'comfortable',
  sidebar: 'full',
  showNews: true,
  dark: false,
  accent: '#FF6428',
  liveTicker: true,
};

export default function App() {
  return (
    <RouteTitleProvider>
      <AuthGate>
        {(user, signOut) => <AuthedApp user={user} onSignOut={signOut}/>}
      </AuthGate>
    </RouteTitleProvider>
  );
}

// Routes that render but aren't in the sidebar NAV. Keeps deep-links
// to them working (skills hash from "+ Draft skill" etc.) and lets
// us teach the App about new top-level views without touching shell.
const EXTRA_ROUTES = new Set(['skills', 'workers', 'profile', 'runs', 'chat-artefacts', 'admin-readme', 'personal-agents', 'slide-sandbox']);

/** Split a hash into `{ route, subPath }`. `#clients/acme` →
 *  `{ route: 'clients', subPath: 'acme' }`. Empty subPath when the
 *  hash has no slash. Validates the route against NAV + EXTRA_ROUTES
 *  and falls back to null/null if unknown. */
function parseHash(h) {
  const cleaned = (h || '').replace(/^#/, '');
  if (!cleaned) return { route: null, subPath: '' };
  const idx = cleaned.indexOf('/');
  const head = idx === -1 ? cleaned : cleaned.slice(0, idx);
  const rest = idx === -1 ? '' : cleaned.slice(idx + 1);
  const isKnown = !!NAV.find(n => n.id === head) || EXTRA_ROUTES.has(head);
  return isKnown ? { route: head, subPath: rest } : { route: null, subPath: '' };
}

// Small circular avatar for the presence stack. Falls back to initials
// on a tinted disc when the person hasn't uploaded a photo (mirrors the
// Avatar primitive, but sized down to ~20px for the ticker).
function MiniAvatar({ src, name, ring }) {
  const [failed, setFailed] = React.useState(false);
  const common = {
    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
    border: `2px solid ${ring}`,
  };
  if (src && !failed) {
    return <img src={src} alt={name} title={name} onError={() => setFailed(true)} style={{...common, objectFit:'cover', background:'var(--bg-card)'}}/>;
  }
  const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('');
  return (
    <div title={name} style={{
      ...common, background:'var(--bg-card)', color:'var(--fg-muted)',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:8, fontWeight:700,
    }}>{initials}</div>
  );
}

// "N online" + a stack of the 10 most-recently-active faces, rendered
// next to the live indicator. Polls /api/presence every minute (the
// online window is 10 minutes, so a slow poll is plenty fresh).
function LivePresence() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      getPresence()
        .then(d => { if (!cancelled) setData(d); })
        .catch(() => { /* best-effort — ticker stays as-is on failure */ });
    };
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data || !data.count) return null;
  const people = data.people || [];
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:8}}>
      <span style={{color:'var(--fg-muted)'}}>·</span>
      <span>
        <strong>{data.count}</strong>
        <span style={{color:'var(--fg-muted)'}}> online</span>
      </span>
      {people.length > 0 && (
        <span style={{display:'inline-flex',alignItems:'center'}} aria-label={`Online: ${people.map(p => p.name).join(', ')}`}>
          {people.map((p, i) => (
            <span key={p.id} style={{marginLeft: i === 0 ? 0 : -6, zIndex: people.length - i}}>
              <MiniAvatar src={p.photoUrl} name={p.name} ring="var(--bg)"/>
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

// Live banner. Content always starts left-aligned. When it's wider than the
// bar (e.g. on mobile), it slowly scrolls LEFT until the end is reached, then
// back to the start, and repeats (ping-pong). When it fits, it stays put.
// The exact overflow distance is measured and fed to the CSS animation.
function LiveTicker({ children }) {
  const wrapRef = React.useRef(null);
  const trackRef = React.useRef(null);
  const [shift, setShift] = React.useState(0);  // px the content overflows by; 0 = fits
  React.useEffect(() => {
    const check = () => {
      const wrap = wrapRef.current, track = trackRef.current;
      if (!wrap || !track) return;
      const cs = getComputedStyle(wrap);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const over = track.scrollWidth - (wrap.clientWidth - padX);
      setShift(over > 4 ? Math.ceil(over) : 0);
    };
    check();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(check);
      if (wrapRef.current) ro.observe(wrapRef.current);
      if (trackRef.current) ro.observe(trackRef.current);  // content width can change (presence/build)
    }
    window.addEventListener('resize', check);
    const t = setInterval(check, 30_000);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', check); clearInterval(t); };
  }, []);
  const marquee = shift > 0;
  // Constant ~22px/s travel so longer overflow scrolls for longer (still slow).
  const dur = Math.max(7, Math.round(shift / 22));
  return (
    <div className={`ticker ${marquee ? 'ticker--marquee' : ''}`} ref={wrapRef}>
      <div
        className="ticker__track"
        ref={trackRef}
        style={marquee ? { '--ticker-shift': `${shift}px`, animationDuration: `${dur}s` } : undefined}
      >
        {children}
      </div>
    </div>
  );
}

// The standalone "Profile" surface was merged into the unified people
// page. The #profile route just bounces you to your own people page,
// where self-only sections (settings, compliance, insights) render.
//
// The redirect drives the app's route state (not location.hash directly):
// writing the hash here races with — and loses to — AuthedApp's hash-mirror
// effect, which would revert it back to #profile while route state still
// reads 'profile', leaving us stuck on the spinner. Flipping route state
// instead lets the mirror effect write the correct #people/<id> hash.
function ProfileRedirect({ userId, onResolve }) {
  React.useEffect(() => {
    if (userId) onResolve(userId);
  }, [userId, onResolve]);
  return <div className="page"><div className="lede" style={{padding:24}}>Opening your profile…</div></div>;
}

function AuthedApp({ user: authedUser, onSignOut }) {
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const initial = parseHash(location.hash);
  const mainScrollRef = React.useRef(null);
  const [route, setRoute] = React.useState(initial.route || 'news');
  const [subPath, setSubPath] = React.useState(initial.subPath || '');
  const [tweaksOpen, setTweaksOpen] = React.useState(false);
  const [navOpen, setNavOpen] = React.useState(false);
  // Bumped when a sidebar item for the route you're ALREADY on is
  // clicked. It feeds the content key so the active view remounts back
  // to its top-level state — see navigateTo + the keyed wrapper below.
  const [resetNonce, setResetNonce] = React.useState(0);
  // Latest route, read inside the (stable) navigateTo callback without
  // forcing it to re-create on every route change.
  const routeRef = React.useRef(route);
  routeRef.current = route;
  // Auto-close the mobile nav when the route changes — otherwise it
  // stays open after the user picks something and obscures the new
  // page.
  React.useEffect(() => { setNavOpen(false); }, [route]);
  // Snapshot of the unread badge for the route BEFORE we open it, so
  // we can render an "N new since you last visited" banner the first
  // time the user lands there in a session.
  // (Per-route "N new" banner state removed by user request — the
  // notification bell in the topbar carries the unread surface.)

  // Reflect route+subPath back into the URL hash on state change.
  // We DON'T write back when the parsed hash already matches state —
  // otherwise the hashchange listener and this effect ping-pong.
  React.useEffect(() => {
    const target = subPath ? `${route}/${subPath}` : route;
    if (location.hash.replace(/^#/, '') !== target) location.hash = target;
  }, [route, subPath]);

  // Browser back/forward + cross-view nav (notification URLs, "Open
  // Intents" CTA, "+ Draft skill" from a coverage gap, future
  // "open client detail" links) all change location.hash directly.
  // Mirror that back into route + subPath state so the view switches.
  React.useEffect(() => {
    const onHashChange = () => {
      const parsed = parseHash(location.hash);
      if (!parsed.route) return;
      if (parsed.route !== route)     setRoute(parsed.route);
      if (parsed.subPath !== subPath) setSubPath(parsed.subPath);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [route, subPath]);

  // Wrap setRoute so sidebar / nav clicks clear the sub-path
  // (otherwise jumping from #clients/acme to #people would leave
  // subPath="acme" lingering for People to interpret).
  //
  // Clicking the nav item for the route you're already on must take you
  // back to that section's top level — not silently no-op. Clearing the
  // sub-path handles detail pages (e.g. #people/<id> → #people), but
  // views that hold their own sub-navigation in internal state (Agents
  // tabs, OrgRepo elements, Admin tabs, the selected briefing in News…)
  // wouldn't reset, because route+subPath are unchanged so nothing
  // remounts. Bump resetNonce on a same-route click to force the active
  // view to remount to its default, top-level state. Also close the
  // mobile nav, which otherwise stays open (its effect keys on `route`).
  const navigateTo = React.useCallback(next => {
    if (routeRef.current === next) setResetNonce(n => n + 1);
    setNavOpen(false);
    setSubPath('');
    setRoute(next);
  }, []);

  // Resolve the #profile bounce to the signed-in user's people detail by
  // flipping route state (route + subPath together) rather than poking the
  // hash — the hash-mirror effect above then deep-links #people/<id>.
  const openSelfProfile = React.useCallback(id => {
    setRoute('people');
    setSubPath(id);
  }, []);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-density', tweaks.density);
    document.documentElement.setAttribute('data-theme', tweaks.dark ? 'dark' : 'light');
    document.documentElement.style.setProperty('--orange', tweaks.accent);
  }, [tweaks.density, tweaks.dark, tweaks.accent]);

  // iOS PWA "header drifts up under the status bar" guard.
  //
  // Why it happens: the app pins the whole UI to the device height
  // (100lvh, overflow:hidden) and scrolls only inside .main-scroll. In an
  // installed PWA, iOS still scrolls the *document* in two cases — (1) when
  // a text input is focused it lifts the field above the keyboard, and the
  // offset isn't always restored when the keyboard closes; (2) on resume /
  // app-switch / rotation it can restore with a stale offset. Either way the
  // header + hamburger end up off the top until a restart.
  //
  // The body is pinned (position:fixed) in CSS so it has no scrollable
  // overflow; this snaps any residual document scroll back to 0 on every
  // trigger (visibility, focus changes, keyboard show/hide, rotation).
  React.useEffect(() => {
    const reset = () => {
      if (window.scrollX || window.scrollY) window.scrollTo(0, 0);
      const el = document.scrollingElement;
      if (el && el.scrollTop) el.scrollTop = 0;
      if (document.body.scrollTop) document.body.scrollTop = 0;
    };
    const resetSoon = () => { reset(); setTimeout(reset, 60); setTimeout(reset, 250); };
    const onVisible = () => { if (!document.hidden) resetSoon(); };
    window.addEventListener('pageshow', resetSoon);
    window.addEventListener('focus', resetSoon);
    window.addEventListener('orientationchange', resetSoon);
    document.addEventListener('visibilitychange', onVisible);
    // Field blur (keyboard dismiss) is the most frequent trigger.
    document.addEventListener('focusout', resetSoon);
    const vv = window.visualViewport;
    if (vv) { vv.addEventListener('resize', reset); vv.addEventListener('scroll', reset); }
    return () => {
      window.removeEventListener('pageshow', resetSoon);
      window.removeEventListener('focus', resetSoon);
      window.removeEventListener('orientationchange', resetSoon);
      document.removeEventListener('visibilitychange', onVisible);
      document.removeEventListener('focusout', resetSoon);
      if (vv) { vv.removeEventListener('resize', reset); vv.removeEventListener('scroll', reset); }
    };
  }, []);

  // Merge session user with display defaults. photoUrl comes from
  // /api/auth/me (now includes the live R2 URL); fall back to the
  // placeholder portrait until the user uploads their own.
  const user = {
    ...authedUser,
    company: authedUser.company || 'Eraneos',
    img: authedUser.photoUrl || '/assets/portrait-1.png',
  };

  const handleSignOut = async () => {
    await apiLogout();
    onSignOut();
  };

  // Unread badges per nav route. lastSeen is persisted in localStorage;
  // when you open a route we stamp it with Date.now() so the next poll
  // shows that bucket empty until something new lands.
  const [lastSeen, setLastSeen] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(SEEN_LS_KEY) || '{}') || {}; }
    catch { return {}; }
  });
  const [unread, setUnread] = React.useState({});
  // Bumping this state forces an immediate /api/me/unread refetch — used
  // when an explicit user action (e.g. dismiss banner) should clear the
  // counters without waiting for the next 30s tick.
  const [unreadTick, setUnreadTick] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      fetchUnreadCounts(lastSeen)
        .then(c => { if (!cancelled) setUnread(c); })
        .catch(() => { /* swallow — badges are best-effort */ });
    };
    refresh();
    const t = setInterval(refresh, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [lastSeen, unreadTick]);

  // Stamp lastSeen the moment a route is selected — handles both
  // sidebar clicks and external hash changes via the same path.
  // (The per-route "N new" banner used to read the carryover here;
  // the banner was removed per user request, so we just stamp.)
  React.useEffect(() => {
    setLastSeen(prev => {
      const next = { ...prev, [route]: Date.now() };
      try { localStorage.setItem(SEEN_LS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  // We deliberately don't depend on `unread` here — the unread state at
  // the moment the route changes is what we want.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  // Unread MESSAGES (per-chat). The /api/me/unread `chat` bucket is
  // route-level (chats touched since you last opened the Chats section);
  // for the orange per-chat marks in the history list we track read state
  // per chat in localStorage (src/lib/chat-reads). We poll the chat list
  // here too so the sidebar Chats badge, the topbar messages icon and the
  // mobile hamburger dot all read the same per-chat count as the list.
  const [chats, setChats] = React.useState([]);
  const [reads, setReads] = React.useState(getReads());
  React.useEffect(() => subscribeReads(setReads), []);
  // Pull the server's per-chat read map once on load so this browser shows
  // the same unread marks as every other browser the user is signed into.
  React.useEffect(() => { syncReadsFromServer(); }, []);
  React.useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      listChats()
        .then(({ chats: rows }) => {
          if (cancelled) return;
          const list = rows || [];
          setChats(list);
        })
        .catch(() => { /* best-effort — badges degrade gracefully */ });
    };
    refresh();
    const t = setInterval(refresh, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [unreadTick, route]);
  const unreadMessages = countUnread(chats, reads);

  // Per-chat message unreads drive the Chats badge (consistent with the
  // list marks); other buckets keep their /api/me/unread counts.
  const navUnread = { ...unread, chat: unreadMessages };

  return (
    <div className="app brand-eraneos">
      <Sidebar route={route} onRoute={navigateTo} user={user} sidebar={tweaks.sidebar} onSignOut={handleSignOut} unread={navUnread} mobileOpen={navOpen}/>
      <div className={`nav-scrim ${navOpen ? 'is-open' : ''}`} onClick={() => setNavOpen(false)}/>

      <div className="main-col">
        <TopHeader
          route={route}
          subPath={subPath}
          onRoute={navigateTo}
          messagesUnread={unreadMessages}
          onToggleNav={() => setNavOpen(v => !v)}
          /* tweaks trigger hidden — settings still apply from
             persisted localStorage; the gear icon is gone from the
             topbar by user request. */
          tweaksTrigger={null}
        />

        {tweaks.liveTicker && (
          <LiveTicker>
            <span className="live-dot"/>
            <strong>Live · </strong>
            <span style={{color:'var(--fg-muted)'}}>eOS is live · D1 connected · agents idle</span>
            <LivePresence/>
            <span
              title={`Build ${BUILD_SHA}\nBuilt ${BUILD_TIME}`}
              style={{marginLeft:'auto',color:'var(--fg-muted)',letterSpacing:'0.04em',opacity:0.7,fontFamily:'ui-monospace, SFMono-Regular, monospace',fontSize:11}}
            >
              build {BUILD_SHA_SHORT}
            </span>
            <span
              style={{marginLeft:8,color:'var(--orange)',letterSpacing:'0.04em',fontFamily:'ui-monospace, SFMono-Regular, monospace',fontSize:11}}
            >
              //Test Environment - Do Not Use Sensitive Data
            </span>
          </LiveTicker>
        )}

        {/* Per-route "N new since your last visit" banner removed
            by user request. Unread counts are surfaced only via the
            NotificationBell in the topbar (top-right). */}

        {/* Pull-to-refresh on every route EXCEPT chat. Chat has its
            own scrollable surface and the gesture was reloading the
            page when the user tried to scroll within messages. */}
        {route !== 'chat' && <PullToRefresh scrollRef={mainScrollRef}/>}
        <div ref={mainScrollRef} className={`main-scroll${route === 'chat' ? ' main-scroll--no-scroll' : ''}`}>
          {/* Key on route + resetNonce: route changes already swap the
              rendered view, and a same-route nav click bumps resetNonce
              to remount it back to its top level. subPath is deliberately
              NOT in the key, so drilling into a detail page (e.g.
              #people/<id>) re-renders in place rather than tearing down
              the tree. */}
          <React.Fragment key={`${route}:${resetNonce}`}>
          {route === 'chat' && <ChatView user={user} autoOpenChat={subPath ? `live:${subPath}` : undefined}/>}
          {(route === 'agents' || route === 'personal-agents') && <AgentsView currentUser={user} subPath={subPath}/>}
          {route === 'workers' && <WorkersView/>}
          {route === 'reports' && <ReportsView/>}
          {route === 'skills' && <SkillsView currentUser={user}/>}
          {route === 'news' && <NewsView/>}
          {route === 'logs' && <LogsView/>}
          {route === 'admin-logs' && user.role === 'admin' && <AdminLogsView/>}
          {route === 'admin-readme' && user.role === 'admin' && <AdminReadmeView/>}
          {route === 'onboarding' && user.role === 'admin' && <OnboardingView/>}
          {route === 'people' && <PeopleView currentUser={user} subPath={subPath} onNavigate={p => setSubPath(p)}/>}
          {route === 'projects' && <ProjectsView currentUser={user} subPath={subPath} onNavigate={p => setSubPath(p)}/>}
          {route === 'clients' && <ClientsView currentUser={user} subPath={subPath} onNavigate={p => setSubPath(p)}/>}
          {route === 'services' && <ServicesView currentUser={user} subPath={subPath} onNavigate={p => setSubPath(p)}/>}
          {route === 'industries' && <IndustriesView currentUser={user} subPath={subPath} onNavigate={p => setSubPath(p)}/>}
          {route === 'organizations' && <OrganizationsView currentUser={user} subPath={subPath} onNavigate={p => setSubPath(p)}/>}
          {route === 'offices' && <OfficesView currentUser={user} subPath={subPath} onNavigate={p => setSubPath(p)}/>}
          {route === 'staffing' && <StaffingView/>}
          {route === 'orgrepo' && user.role === 'admin' && <OrgRepoView/>}
          {route === 'slide-sandbox' && user.role === 'admin' && <SlideSandboxView subPath={subPath} currentUser={user}/>}
          {route === 'profile' && <ProfileRedirect userId={user._id || user.id} onResolve={openSelfProfile}/>}
          {route === 'admin' && user.role === 'admin' && <AdminView currentUser={user}/>}
          {/* runs/<runId>/(deck|memo) → agent-keyed viewer.
              chat-artefacts/<messageId>/(deck|memo) → chat-keyed viewer.
              Both render the same components — mode prop swaps URL bases.
              `.split('?')[0]` strips the ?agent=<id> query string that
              spawn-* skills append to drive the chat inline trace. */}
          {route === 'runs' && (subPath || '').split('/')[1]?.split('?')[0] === 'memo'
            ? <MemoView subPath={subPath} mode="agent"/>
            : route === 'runs' && <DeckView subPath={subPath} mode="agent"/>}
          {route === 'chat-artefacts' && (subPath || '').split('/')[1]?.split('?')[0] === 'memo'
            ? <MemoView subPath={subPath} mode="chat"/>
            : route === 'chat-artefacts' && <DeckView subPath={subPath} mode="chat"/>}
          </React.Fragment>
        </div>
      </div>

      <TweaksPanel open={tweaksOpen} onOpenChange={setTweaksOpen}>
        <TweakSection title="Layout">
          <TweakRadio label="Density" value={tweaks.density} onChange={v => setTweak('density', v)} options={[{value:'comfortable',label:'Comfortable'},{value:'compact',label:'Compact'}]}/>
          <TweakRadio label="Sidebar" value={tweaks.sidebar} onChange={v => setTweak('sidebar', v)} options={[{value:'full',label:'Full'},{value:'compact',label:'Compact'}]}/>
          <TweakToggle label="Live ticker" value={tweaks.liveTicker} onChange={v => setTweak('liveTicker', v)}/>
          <TweakToggle label="Dark" value={tweaks.dark} onChange={v => setTweak('dark', v)}/>
        </TweakSection>

        <TweakSection title="Accent">
          <TweakColor
            value={tweaks.accent}
            onChange={v => setTweak('accent', v)}
            options={['#FF6428','#715AAE','#A20235','#1A1414']}
          />
        </TweakSection>

        <TweakSection title="Shortcuts">
          <TweakButton onClick={() => navigateTo('skills')}>Jump to Skills</TweakButton>
          <TweakButton onClick={() => navigateTo('admin')}>Jump to Admin</TweakButton>
          <TweakButton onClick={() => navigateTo('orgrepo')}>Jump to OrgRepo</TweakButton>
        </TweakSection>
      </TweaksPanel>
    </div>
  );
}
